/**
 * Institution Hooks
 *
 * React Query hooks for institution management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as institutionService from './institution.service'
import type { OnboardData } from './institution.service'

export function useGlobalCatalog(enabled: boolean) {
  return useQuery({
    queryKey: ['institutions', 'catalog', 'global'],
    queryFn: () => institutionService.getGlobalCatalog(),
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}
import { tokenStorage } from '@/lib/api'
import type {
  CreateInstitutionData,
  UpdateInstitutionData,
  InstitutionSearchFilters,
} from '@/domain/institution/types'

// Query keys
export const institutionKeys = {
  all: ['institutions'] as const,
  lists: () => [...institutionKeys.all, 'list'] as const,
  list: (filters: InstitutionSearchFilters) => [...institutionKeys.lists(), filters] as const,
  details: () => [...institutionKeys.all, 'detail'] as const,
  detail: (id: string) => [...institutionKeys.details(), id] as const,
  stats: (id: string) => [...institutionKeys.all, 'stats', id] as const,
  globalStats: () => [...institutionKeys.all, 'globalStats'] as const,
}

/**
 * Hook to fetch institutions list
 */
export function useInstitutions(filters?: InstitutionSearchFilters) {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: institutionKeys.list(filters ?? {}),
    queryFn: () => institutionService.getInstitutions(filters),
    enabled: hasToken,
  })
}

/**
 * Hook to fetch single institution
 */
export function useInstitution(id: string) {
  return useQuery({
    queryKey: institutionKeys.detail(id),
    queryFn: () => institutionService.getInstitutionById(id),
    enabled: !!id,
  })
}

/**
 * Hook to fetch institution stats
 */
export function useInstitutionStats(id: string) {
  return useQuery({
    queryKey: institutionKeys.stats(id),
    queryFn: () => institutionService.getInstitutionStats(id),
    enabled: !!id,
  })
}

/**
 * Hook to fetch global stats
 */
export function useGlobalStats() {
  const hasToken = !!tokenStorage.getAccessToken()

  return useQuery({
    queryKey: institutionKeys.globalStats(),
    queryFn: () => institutionService.getGlobalStats(),
    refetchInterval: 60000, // Refresh every minute
    enabled: hasToken,
  })
}

/**
 * Hook to create institution
 */
export function useCreateInstitution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateInstitutionData) => institutionService.createInstitution(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: institutionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: institutionKeys.globalStats() })
    },
  })
}

/**
 * Hook to update institution
 */
/**
 * Catalogo de modulos habilitables, servido por el backend.
 *
 * No se escribe aqui una copia de la lista: dos listas de lo mismo acaban
 * discrepando y alguien termina viendo un modulo que ya no existe.
 */
export function useCatalogoDeModulos() {
  return useQuery({
    queryKey: ['institutions', 'modulos'],
    queryFn: () => institutionService.obtenerCatalogoDeModulos(),
    staleTime: 1000 * 60 * 60,
  })
}

export function useUpdateInstitution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInstitutionData }) =>
      institutionService.updateInstitution(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: institutionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: institutionKeys.detail(variables.id) })
    },
  })
}

/**
 * Hook to delete institution
 */
export function useDeleteInstitution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => institutionService.deleteInstitution(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: institutionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: institutionKeys.globalStats() })
    },
  })
}

/**
 * Hook to activate institution
 */
export function useActivateInstitution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => institutionService.activateInstitution(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: institutionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: institutionKeys.globalStats() })
    },
  })
}

/**
 * Hook to deactivate institution
 */
export function useDeactivateInstitution() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => institutionService.deactivateInstitution(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: institutionKeys.lists() })
      queryClient.invalidateQueries({ queryKey: institutionKeys.globalStats() })
    },
  })
}

/**
 * Hook to onboard institution with global catalog
 */
export function useOnboardInstitution() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data?: OnboardData }) =>
      institutionService.onboardInstitution(id, data),
  })
}
